import {
  GuaranteesFetchStatus,
  TGuaranteesAsyncSlice,
  TGuaranteesPageStore,
} from './types.ts';
import {
  guaranteesSupervisorGroupKeys,
} from './constants.ts';
import { supervisorAdministrationPageKeys } from '~/modules/supervisor-administration/config.ts';

export function makeGuaranteesRequestState<T>(emptyData: T): TGuaranteesAsyncSlice<T> {
  return {
    data: emptyData,
    status: GuaranteesFetchStatus.Idle,
    error: null,
  };
}

const makeAdministrationSupervisorInfoByPage = () =>
  supervisorAdministrationPageKeys.reduce(
    (acc, pageKey) => ({
      ...acc,
      [pageKey]: makeGuaranteesRequestState(null),
    }),
    {} as TGuaranteesPageStore['administrationSupervisorInfoByPage']
  );

const makeSupervisorSegmentsByGroup = () =>
  guaranteesSupervisorGroupKeys.reduce(
    (acc, groupKey) => ({
      ...acc,
      [groupKey]: makeGuaranteesRequestState([]),
    }),
    {} as TGuaranteesPageStore['supervisorSegmentsByGroup']
  );

export const initialState: TGuaranteesPageStore = {
  applications: makeGuaranteesRequestState({
    applications: [],
    count: 0,
    pageConfiguration: {
      page: 0,
      size: 10,
      totalElements: 0,
    },
  }),
  applicationTimeline: makeGuaranteesRequestState([]),
  applicationFileCapabilities: makeGuaranteesRequestState({
    fileTypes: [],
  }),
  applicationFileTypeCapabilities: makeGuaranteesRequestState({
    fileTypes: [],
  }),
  uploadApplicationFile: makeGuaranteesRequestState(null),
  regenerateApplicationFile: makeGuaranteesRequestState(null),
  patchApplicationFileCapabilities: makeGuaranteesRequestState(null),
  downloadApplicationFile: makeGuaranteesRequestState(null),
  deleteApplicationFile: makeGuaranteesRequestState(null),
  createApplication: makeGuaranteesRequestState(null),
  currentApplication: makeGuaranteesRequestState(null),
  patchRootApplicationSection: makeGuaranteesRequestState(null),
  principal: makeGuaranteesRequestState(null),
  patchPrincipal: makeGuaranteesRequestState(null),
  beneficiary: makeGuaranteesRequestState([]),
  patchBeneficiary: makeGuaranteesRequestState(null),
  baseRelations: makeGuaranteesRequestState(null),
  patchBaseRelations: makeGuaranteesRequestState(null),
  guaranteeForm: makeGuaranteesRequestState(null),
  patchGuaranteeForm: makeGuaranteesRequestState(null),
  mainDealConditions: makeGuaranteesRequestState(null),
  patchMainDealConditions: makeGuaranteesRequestState(null),
  managerTerms: makeGuaranteesRequestState(null),
  patchManagerTerms: makeGuaranteesRequestState(null),
  executionForm: makeGuaranteesRequestState(null),
  patchExecutionForm: makeGuaranteesRequestState(null),
  patchIssueGuarantee: makeGuaranteesRequestState(null),
  patchApplicationTender: makeGuaranteesRequestState(null),
  applicationTenderInfo: makeGuaranteesRequestState(null),
  fetchProzorroTender: makeGuaranteesRequestState(null),
  patchApplicationMainDeal: makeGuaranteesRequestState(null),
  mainDeals: makeGuaranteesRequestState({
    content: [],
    totalPages: 0,
    totalElements: 0,
    number: 0,
    size: 20,
    last: true,
  }),
  mainDealsRequestKey: null,
  accounts: makeGuaranteesRequestState({
    content: [],
    totalPages: 0,
    totalElements: 0,
    number: 0,
    size: 0,
    last: true,
  }),
  principalDebitAccounts: makeGuaranteesRequestState({
    content: [],
    totalPages: 0,
    totalElements: 0,
    number: 0,
    size: 0,
    last: true,
  }),
  securityCurrencies: makeGuaranteesRequestState(null),
  putSecurityCurrencies: makeGuaranteesRequestState(null),
  udbtfTemplateDocument: makeGuaranteesRequestState(null),
  amTemplateDocument: makeGuaranteesRequestState(null),
  conclusionTemplateDocument: makeGuaranteesRequestState(null),
  approvalComment: makeGuaranteesRequestState(null),
  generateConclusionAttachment: makeGuaranteesRequestState(null),
  patchApplicationSecret: makeGuaranteesRequestState(null),
  checkApplicationRequest: makeGuaranteesRequestState(null),
  managerSupervisorInfo: makeGuaranteesRequestState(null),
  uakdoSupervisorInfo: makeGuaranteesRequestState(null),
  administrationSupervisorInfoByPage: makeAdministrationSupervisorInfoByPage(),
  managerTasks: makeGuaranteesRequestState({
    tasks: [],
    count: 0,
    pageConfiguration: {
      page: 0,
      size: 5,
      totalElements: 0,
    },
  }),
  administrationRoles: makeGuaranteesRequestState({
    roles: [],
  }),
  administrationTasks: makeGuaranteesRequestState({
    tasks: [],
    count: 0,
    pageConfiguration: {
      page: 0,
      size: 5,
      totalElements: 0,
    },
  }),
  updateApplicationStatus: makeGuaranteesRequestState(null),
  taskSupervisors: makeGuaranteesRequestState(null),
  patchApplicationAssignee: makeGuaranteesRequestState(null),
  updateUdbtfApprovalStatus: makeGuaranteesRequestState(null),
  updateUoApprovalStatus: makeGuaranteesRequestState(null),
  updateUmvnApprovalStatus: makeGuaranteesRequestState(null),
  submitApplicationByClient: makeGuaranteesRequestState(null),
  submitApprovalLetterByClient: makeGuaranteesRequestState(null),
  submitApplicationByManager: makeGuaranteesRequestState(null),
  submitApplicationByUdbtf: makeGuaranteesRequestState(null),
  submitApprovalLetterByUdbtf: makeGuaranteesRequestState(null),
  submitApprovalLetterByUsapok: makeGuaranteesRequestState(null),
  submitApplicationByUakdo: makeGuaranteesRequestState(null),
  submitApplicationByUakdoController: makeGuaranteesRequestState(null),
  rejectApplicationByUakdoController: makeGuaranteesRequestState(null),
  submitApplicationByAmManager: makeGuaranteesRequestState(null),
  submitApprovalLetterByAmManager: makeGuaranteesRequestState(null),
  supervisorSegments: makeGuaranteesRequestState([]),
  supervisorSegmentsByGroup: makeSupervisorSegmentsByGroup(),
};
